import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Editor } from '@tiptap/react';

interface EditorContextValue {
  documentEditor: Editor | null;
  setDocumentEditor: (editor: Editor | null) => void;
  activeEditor: Editor | null;
  setActiveEditor: (editor: Editor | null) => void;
  boxSignStatus: string | null;
  setBoxSignStatus: (status: string | null) => void;
  productPickerTableId: string | null;
  setProductPickerTableId: (tableId: string | null) => void;
}

const EditorContext = createContext<EditorContextValue>({
  documentEditor: null,
  setDocumentEditor: () => {},
  activeEditor: null,
  setActiveEditor: () => {},
  boxSignStatus: null,
  setBoxSignStatus: () => {},
  productPickerTableId: null,
  setProductPickerTableId: () => {},
});

export function EditorProvider({
  children,
  boxSignStatus: externalStatus,
}: {
  children: ReactNode;
  boxSignStatus?: string | null;
}) {
  const [documentEditor, setDocumentEditor] = useState<Editor | null>(null);
  const [activeEditor, setActiveEditorState] = useState<Editor | null>(null);
  const [boxSignStatus, setBoxSignStatusState] = useState<string | null>(externalStatus ?? null);
  const [productPickerTableId, setProductPickerTableId] = useState<string | null>(null);

  useEffect(() => {
    if (externalStatus !== undefined) {
      setBoxSignStatusState(externalStatus);
    }
  }, [externalStatus]);

  const setActiveEditor = useCallback((editor: Editor | null) => {
    setActiveEditorState(editor);
  }, []);

  const setBoxSignStatus = useCallback((status: string | null) => {
    setBoxSignStatusState(status);
  }, []);

  return (
    <EditorContext.Provider
      value={{
        documentEditor,
        setDocumentEditor,
        activeEditor,
        setActiveEditor,
        boxSignStatus,
        setBoxSignStatus,
        productPickerTableId,
        setProductPickerTableId,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
}

export function useActiveEditor() {
  return useContext(EditorContext);
}

export function useBoxSignStatus() {
  const { boxSignStatus } = useContext(EditorContext);
  return boxSignStatus;
}
